import { retryEquitasSpEnrichment, fetchAllJourneyData } from "./apiservice"

/**
 * Triggers the Equitas SP enrichment API (fire-and-forget).
 * Called on page load for Equitas channel to force sync SP data in database.
 *
 * @param {string} applicationNumber - The application number
 */
export const triggerSpEnrichment = (applicationNumber) => {
  if (!applicationNumber) {
    console.warn("triggerSpEnrichment: applicationNumber is required")
    return
  }

  const payload = {
    applicationNumber,
    triggerSource: "frontend"
  }

  retryEquitasSpEnrichment(payload, { sync: true })
    .then(res => {
      console.log("SP enrichment triggered successfully", res)
    })
    .catch(err => {
      // Fire-and-forget — log error but do not throw
      console.error("SP enrichment trigger failed (non-blocking)", err)
    })
}

/**
 * Polls the Equitas SP enrichment API with sync=true&spSync=true.
 * Calls onDetailsAvailable when API returns status: true with SP details.
 *
 * @param {string} applicationNumber - The application number
 * @param {function} onDetailsAvailable - Callback with SP data when details are available
 * @returns {function} Cleanup function to stop polling
 */
export const startSpEnrichmentPolling = (applicationNumber, onDetailsAvailable) => {
  const INTERVAL_MS = 10000
  const payload = { applicationNumber, triggerSource: "frontend" }
  const params = { sync: true, spSync: true }

  const poll = () => {
    retryEquitasSpEnrichment(payload, params)
      .then(res => {
        if (res?.status === true) {
          onDetailsAvailable(res?.data)
        }
      })
      .catch(err => {
        console.error("SP enrichment polling error (non-blocking)", err)
      })
  }

  poll()
  const id = setInterval(poll, INTERVAL_MS)
  return () => clearInterval(id)
}

/**
 * Re-syncs SP details via sync=true API and validates the response.
 * Used before submit to force fresh enrichment and verify SP details exist.
 *
 * @param {string} applicationNumber - The application number
 * @returns {Promise<{ isValid: boolean, missingFields: string[], data: object }>}
 */
export const verifySpDetailsWithSync = async (applicationNumber) => {
  if (!applicationNumber) {
    return { isValid: false, missingFields: ["Application number missing"], data: null }
  }

  try {
    const payload = { applicationNumber, triggerSource: "frontend" }
    const res = await retryEquitasSpEnrichment(payload, { sync: true })

    if (res?.status === true && !!res?.data?.SPmobileNumberISNP && !!res?.data?.SPemailIDISNP) {
      return { isValid: true, missingFields: [], data: res?.data }
    }

    return {
      isValid: false,
      missingFields: ["SP Phone Number", "SP Email"],
      data: res?.data
    }
  } catch (err) {
    console.error("verifySpDetailsWithSync: Error syncing SP details", err)
    // On error, allow submission to proceed (don't block on network failures)
    return { isValid: true, missingFields: [], data: null }
  }
}

/**
 *
 * @param {object} journeyData - The journey data object from fetchAllJourneyData
 * @returns {{ isValid: boolean, missingFields: string[] }}
 */
export const validateSpDetails = (journeyData) => {
  const spPhone = journeyData?.agentDetails?.spPh
  const spEmail = journeyData?.agentDetails?.spMail
  const missingFields = []

  if (!spPhone) {
    missingFields.push("SP Phone Number")
  }
  if (!spEmail) {
    missingFields.push("SP Email")
  }

  return {
    isValid: missingFields.length === 0,
    missingFields
  }
}

/**
 * Fetches fresh journey data from DB and validates SP details.
 * Used before submit to ensure SP details are up-to-date.
 *
 * @param {string} journeyId - The journey ID
 * @returns {Promise<{ isValid: boolean, missingFields: string[], journeyData: object }>}
 */
export const fetchAndValidateSpDetails = async (journeyId) => {
  if (!journeyId) {
    return { isValid: false, missingFields: ["Journey ID missing"], journeyData: null }
  }

  try {
    const journeyData = await fetchAllJourneyData({ journeyId })
    const validation = validateSpDetails(journeyData)

    return {
      ...validation,
      journeyData
    }
  } catch (err) {
    console.error("fetchAndValidateSpDetails: Error fetching journey data", err)
    // On error, allow submission to proceed (don't block on network failures)
    return { isValid: true, missingFields: [], journeyData: null }
  }
}
